import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../../api/client";
import { useAuth } from "../../../context/AuthContext";
import { useRealtime } from "../../../realtime/useRealtime";
import { OrderCard, StatCard, Btn } from "../../../components/AdminUI";
import { FUNNEL_COLUMNS_UI } from "./funnelColumns";

export default function OwnerDashboard() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [funnel, setFunnel] = useState<{ visitors: any[]; orders: any[]; revenue: number } | null>(null);
  const [tab, setTab] = useState<"board" | "operators" | "packages" | "history" | "settings">("board");

  const refresh = useCallback(() => {
    adminApi.listOrders().then((res) => setOrders(res.orders));
    adminApi.listOperators().then((res) => setOperators(res.operators));
    adminApi.funnel().then(setFunnel);
  }, []);

  useEffect(() => refresh(), [refresh]);
  useRealtime("role:owner", () => refresh());

  const columns = FUNNEL_COLUMNS_UI.map((col) => ({
    ...col,
    orders: orders.filter((o) => col.statuses.includes(o.status)),
  }));

  const counts = {
    completedToday: orders.filter((o) => o.status === "COMPLETED").length,
    problems: orders.filter((o) => ["PAYMENT_NOT_FOUND", "INVALID_CODE", "ACTIVATION_IMPOSSIBLE", "REFUND_PENDING"].includes(o.status)).length,
  };

  return (
    <div className="min-h-screen bg-gm-bg text-gm-text">
      <header className="flex items-center justify-between border-b border-gm-border px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="text-xl font-extrabold">👑 GEO <span className="text-gm-gold">MASTER</span></span>
          <span className="text-xs text-gm-muted">Центр управления</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <nav className="flex gap-2">
            {(["board", "operators", "packages", "history", "settings"] as const).map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`rounded-lg px-3 py-1.5 font-semibold ${tab === t ? "bg-gm-accent text-white" : "text-gm-muted hover:bg-gm-panel2"}`}>
                {t === "board" ? "Заказы" : t === "operators" ? "Операторы" : t === "packages" ? "Пакеты" : t === "history" ? "История" : "Настройки"}
              </button>
            ))}
          </nav>
          <span>{user?.name} · Владелец</span>
          <button onClick={() => logout()} className="text-gm-muted hover:text-gm-text">Выйти</button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          <StatCard label="Новые заказы" value={orders.filter((o) => o.status === "NEW" || o.status === "ASSIGNED").length} />
          <StatCard label="Ожидают оплаты" value={orders.filter((o) => o.status === "AWAITING_PAYMENT").length} />
          <StatCard label="Проверить оплату" value={orders.filter((o) => o.status === "PAYMENT_REVIEW").length} tone="danger" />
          <StatCard label="Ожидают код" value={orders.filter((o) => ["PAYMENT_CONFIRMED", "AWAITING_CODE"].includes(o.status)).length} />
          <StatCard label="Проверить код" value={orders.filter((o) => o.status === "CODE_REVIEW").length} tone="warn" />
          <StatCard label="Завершено" value={counts.completedToday} tone="success" />
          <StatCard label="Проблемные" value={counts.problems} tone="danger" />
        </div>

        {funnel && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard label="Выручка (за период)" value={`${funnel.revenue} ₸`} />
            <StatCard label="Посетителей" value={funnel.visitors.reduce((a, v: any) => a + v.c, 0)} />
            <StatCard label="Конверсия в заказ" value={conversionLabel(funnel)} />
          </div>
        )}

        {tab === "board" && (
          <div className="mt-6 grid grid-cols-1 gap-4 overflow-x-auto lg:grid-cols-6">
            {columns.map((col) => (
              <div key={col.key} className="min-w-[220px] rounded-2xl border border-gm-border bg-gm-panel p-3">
                <div className="mb-3 flex items-center justify-between text-sm font-bold">
                  <span>{col.label}</span>
                  <span className="rounded-full bg-gm-panel2 px-2 py-0.5 text-xs text-gm-muted">{col.orders.length}</span>
                </div>
                <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                  {col.orders.map((o) => (
                    <OwnerOrderCard key={o.id} order={o} operators={operators} onChanged={refresh} />
                  ))}
                  {col.orders.length === 0 && <div className="py-4 text-center text-xs text-gm-muted">Пусто</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "operators" && <OperatorsTab operators={operators} onChanged={refresh} />}
        {tab === "packages" && <PackagesTab />}
        {tab === "history" && <HistoryTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}

function conversionLabel(funnel: { visitors: any[]; orders: any[] }) {
  const visitors = funnel.visitors.reduce((a, v: any) => a + v.c, 0);
  const converted = funnel.visitors.find((v: any) => v.status === "CONVERTED")?.c ?? 0;
  if (!visitors) return "—";
  return `${Math.round((converted / visitors) * 100)}%`;
}

function OwnerOrderCard({ order, operators, onChanged }: { order: any; operators: any[]; onChanged: () => void }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<any>) {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <OrderCard
      order={order}
      footer={
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <select
              value={order.assignedOperatorId ?? ""}
              onChange={async (e) => {
                await adminApi.reassign(order.id, e.target.value || null);
                onChanged();
              }}
              className="w-full rounded-md border border-gm-border bg-gm-bg px-2 py-1 text-xs text-gm-text"
            >
              <option value="">— не назначен —</option>
              {user && <option value={user.id}>{user.name} (я, владелец)</option>}
              {operators.map((op) => (
                <option key={op.id} value={op.id}>{op.name}</option>
              ))}
            </select>
          </div>

          {order.status === "PAYMENT_REVIEW" && (
            <div className="flex gap-2">
              <Btn tone="success" disabled={busy} onClick={() => act(() => adminApi.confirmPayment(order.id))}>Подтвердить</Btn>
              <Btn tone="danger" disabled={busy} onClick={() => act(() => adminApi.paymentNotFound(order.id))}>Не найдена</Btn>
            </div>
          )}
          {order.status === "PAYMENT_CONFIRMED" && (
            <Btn disabled={busy} onClick={() => act(() => adminApi.initiateCode(order.id))}>Я инициировал во внешнем терминале</Btn>
          )}
          {order.status === "CODE_REVIEW" && (
            <div className="rounded-lg border border-gm-gold/40 bg-gm-gold/10 p-2 text-center">
              <CodeInline key={order.updatedAt} orderId={order.id} />
            </div>
          )}
          {order.status === "CODE_REVIEW" && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Btn tone="success" disabled={busy} onClick={() => act(() => adminApi.codeResult(order.id, "valid"))}>Верный</Btn>
                <Btn tone="danger" disabled={busy} onClick={() => act(() => adminApi.codeResult(order.id, "invalid"))}>Неверный</Btn>
              </div>
              <Btn tone="muted" disabled={busy} onClick={() => act(() => adminApi.codeResult(order.id, "impossible"))}>Активация невозможна</Btn>
            </div>
          )}
          {order.status === "INVALID_CODE" && (
            <Btn disabled={busy} onClick={() => act(() => adminApi.codeResend(order.id))}>Запросить новый код</Btn>
          )}
          {order.status === "REFUND_PENDING" && (
            <Btn disabled={busy} onClick={() => act(() => adminApi.refundProcess(order.id))}>Начать возврат</Btn>
          )}
          {order.status === "REFUND_PROCESSING" && (
            <Btn tone="success" disabled={busy} onClick={() => act(() => adminApi.refundComplete(order.id))}>Возврат выполнен</Btn>
          )}
        </div>
      }
    />
  );
}

function CodeInline({ orderId }: { orderId: string }) {
  const [data, setData] = useState<{ code: string } | null>(null);
  useEffect(() => {
    setData(null);
    adminApi.getCode(orderId).then(setData).catch(() => setData(null));
  }, [orderId]);
  if (!data) return <span className="text-xs text-gm-muted">загрузка кода…</span>;
  return <span className="font-mono text-lg font-extrabold tracking-[0.25em] text-gm-gold">{data.code}</span>;
}

function OperatorsTab({ operators, onChanged }: { operators: any[]; onChanged: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi.createOperator({ name, email, password });
      setName(""); setEmail(""); setPassword("");
      onChanged();
    } catch {
      setError("Не удалось создать оператора (пароль минимум 10 символов).");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr,360px]">
      <div className="rounded-2xl border border-gm-border bg-gm-panel p-4">
        <table className="w-full text-sm">
          <thead className="text-left text-gm-muted">
            <tr><th className="pb-2">Имя</th><th>Email</th><th>Статус</th><th>Присутствие</th><th></th></tr>
          </thead>
          <tbody>
            {operators.map((op) => (
              <tr key={op.id} className="border-t border-gm-border">
                <td className="py-2">{op.name}</td>
                <td className="text-gm-muted">{op.email}</td>
                <td>{op.status === "active" ? "Активен" : "Заблокирован"}</td>
                <td>{op.presence === "online" ? "🟢 Онлайн" : "⚪️ Оффлайн"}</td>
                <td>
                  <button
                    onClick={async () => { await adminApi.setOperatorStatus(op.id, op.status === "active" ? "disabled" : "active"); onChanged(); }}
                    className="text-xs font-semibold text-gm-accent underline"
                  >
                    {op.status === "active" ? "Заблокировать" : "Разблокировать"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={create} className="h-fit rounded-2xl border border-gm-border bg-gm-panel p-4">
        <div className="mb-3 font-bold">Новый оператор</div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Имя" required className="mb-2 w-full rounded-lg border border-gm-border bg-gm-panel2 px-3 py-2 text-sm" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" required className="mb-2 w-full rounded-lg border border-gm-border bg-gm-panel2 px-3 py-2 text-sm" />
        <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Временный пароль (мин. 10 символов)" type="password" required className="mb-2 w-full rounded-lg border border-gm-border bg-gm-panel2 px-3 py-2 text-sm" />
        {error && <p className="mb-2 text-xs text-gm-danger">{error}</p>}
        <Btn disabled={busy}>Создать</Btn>
      </form>
    </div>
  );
}

function PackagesTab() {
  const [packages, setPackages] = useState<any[]>([]);
  useEffect(() => { adminApi.listAllPackages().then((res) => setPackages(res.packages)); }, []);

  async function update(pkg: any, patch: Partial<any>) {
    const updated = { ...pkg, ...patch };
    await adminApi.upsertPackage(updated);
    setPackages((prev) => prev.map((p) => (p.id === pkg.id ? updated : p)));
  }

  return (
    <div className="mt-6 rounded-2xl border border-gm-border bg-gm-panel p-4">
      <table className="w-full text-sm">
        <thead className="text-left text-gm-muted"><tr><th className="pb-2">Название</th><th>ГБ</th><th>Цена</th><th>Активен</th></tr></thead>
        <tbody>
          {packages.map((p) => (
            <tr key={p.id} className="border-t border-gm-border">
              <td className="py-2">{p.name}</td>
              <td><input type="number" defaultValue={p.gb} onBlur={(e) => update(p, { gb: Number(e.target.value) })} className="w-20 rounded bg-gm-panel2 px-2 py-1" /></td>
              <td><input type="number" defaultValue={p.price} onBlur={(e) => update(p, { price: Number(e.target.value) })} className="w-24 rounded bg-gm-panel2 px-2 py-1" /></td>
              <td>
                <input type="checkbox" checked={p.active} onChange={(e) => update(p, { active: e.target.checked })} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-gm-muted">Изменение цены не влияет на уже созданные заказы — цена сохраняется как snapshot на момент заказа.</p>
    </div>
  );
}

/** История завершённых/возвращённых/истёкших заказов, сгруппированная по дням. */
function HistoryTab() {
  const [orders, setOrders] = useState<any[] | null>(null);

  useEffect(() => {
    adminApi.orderHistory().then((res) => setOrders(res.orders));
  }, []);

  if (!orders) return <div className="mt-6 text-sm text-gm-muted">Загрузка истории…</div>;
  if (orders.length === 0) return <div className="mt-6 text-sm text-gm-muted">Пока нет завершённых заказов.</div>;

  const groups = new Map<string, any[]>();
  for (const o of orders) {
    const day = new Date(o.updatedAt).toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
    if (!groups.has(day)) groups.set(day, []);
    groups.get(day)!.push(o);
  }

  const STATUS_LABEL: Record<string, string> = {
    COMPLETED: "✅ Завершён",
    REFUNDED: "↩️ Возвращён",
    EXPIRED: "⏱️ Истёк",
    CANCELLED: "✕ Отменён",
  };

  return (
    <div className="mt-6 space-y-6">
      {Array.from(groups.entries()).map(([day, dayOrders]) => (
        <div key={day} className="rounded-2xl border border-gm-border bg-gm-panel p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-bold">{day}</div>
            <div className="text-xs text-gm-muted">{dayOrders.length} заказ(ов)</div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-gm-muted">
              <tr><th className="pb-2">#</th><th>Номер</th><th>Пакет</th><th>Сумма</th><th>Статус</th></tr>
            </thead>
            <tbody>
              {dayOrders.map((o) => (
                <tr key={o.id} className="border-t border-gm-border">
                  <td className="py-2 font-mono text-gm-gold">#{o.shortId}</td>
                  <td className="text-gm-muted">{o.phone}</td>
                  <td>{o.packageGbSnapshot} ГБ</td>
                  <td>{o.packagePriceSnapshot} ₸</td>
                  <td>{STATUS_LABEL[o.status] ?? o.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/** Настройки: ссылки Kaspi/WhatsApp + "спящий режим" (техобслуживание). */
function SettingsTab() {
  const [settings, setSettings] = useState<{ kaspiPaymentUrlTemplate: string; whatsappUrl: string; maintenanceMode: boolean; workingHoursText: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminApi.getSettings().then(setSettings);
  }, []);

  async function save() {
    if (!settings) return;
    setSaving(true);
    setSaved(false);
    try {
      await adminApi.updateSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return <div className="mt-6 text-sm text-gm-muted">Загрузка настроек…</div>;

  return (
    <div className="mt-6 max-w-xl space-y-6">
      <div className="rounded-2xl border border-gm-border bg-gm-panel p-4">
        <div className="mb-3 font-bold">Ссылки</div>
        <label className="mb-1 block text-xs text-gm-muted">Ссылка на оплату Kaspi</label>
        <input
          value={settings.kaspiPaymentUrlTemplate}
          onChange={(e) => setSettings({ ...settings, kaspiPaymentUrlTemplate: e.target.value })}
          className="mb-3 w-full rounded-lg border border-gm-border bg-gm-panel2 px-3 py-2 text-sm"
        />
        <label className="mb-1 block text-xs text-gm-muted">Ссылка на WhatsApp</label>
        <input
          value={settings.whatsappUrl}
          onChange={(e) => setSettings({ ...settings, whatsappUrl: e.target.value })}
          className="w-full rounded-lg border border-gm-border bg-gm-panel2 px-3 py-2 text-sm"
        />
      </div>

      <div className="rounded-2xl border border-gm-border bg-gm-panel p-4">
        <div className="mb-1 font-bold">Спящий режим (техобслуживание)</div>
        <p className="mb-3 text-xs text-gm-muted">
          Пока включено, клиентский сайт показывает сообщение о техработах вместо списка тарифов
          и не создаёт новые заказы.
        </p>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={settings.maintenanceMode}
            onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
          />
          Сайт сейчас "спит" (техобслуживание)
        </label>
        <label className="mb-1 block text-xs text-gm-muted">Текст рабочих часов (показывается клиенту)</label>
        <input
          value={settings.workingHoursText}
          onChange={(e) => setSettings({ ...settings, workingHoursText: e.target.value })}
          placeholder="09:00–23:30"
          className="w-full rounded-lg border border-gm-border bg-gm-panel2 px-3 py-2 text-sm"
        />
      </div>

      <div className="flex items-center gap-3">
        <Btn disabled={saving} onClick={save}>{saving ? "Сохраняем…" : "Сохранить"}</Btn>
        {saved && <span className="text-sm text-gm-success">Сохранено ✓</span>}
      </div>
    </div>
  );
}